import { db, storage } from '../config/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  Timestamp,
  arrayUnion,
  orderBy,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const WIKI_COLLECTION = 'wiki_entries';

/**
 * Creates a new wiki entry
 * @param {object} entryData - { type: 'location'|'person'|'item', title, description, tags, relationships, createdBy, image }
 * @returns {Promise<string>} Entry ID
 */
export const createWikiEntry = async (entryData) => {
  try {
    const entry = {
      ...entryData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      relationships: entryData.relationships || [],
      tags: entryData.tags || [],
      image: entryData.image || null,
    };

    const docRef = await addDoc(collection(db, WIKI_COLLECTION), entry);
    return docRef.id;
  } catch (error) {
    console.error('Error creating wiki entry:', error);
    throw error;
  }
};

/**
 * Updates an existing wiki entry
 * @param {string} entryId - The entry ID
 * @param {object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateWikiEntry = async (entryId, updates) => {
  try {
    const entryRef = doc(db, WIKI_COLLECTION, entryId);
    await updateDoc(entryRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating wiki entry:', error);
    throw error;
  }
};

/**
 * Deletes a wiki entry
 * @param {string} entryId - The entry ID
 * @returns {Promise<void>}
 */
export const deleteWikiEntry = async (entryId) => {
  try {
    // Get entry to find related image and attachments
    const entry = await getWikiEntry(entryId);
    
    // Delete image if exists
    if (entry.image) {
      try {
        await deleteObject(ref(storage, entry.image));
      } catch (storageError) {
        console.warn('Could not delete image:', storageError);
      }
    }

    // Delete all attachments if exist
    if (entry.attachments && entry.attachments.length > 0) {
      for (const attachment of entry.attachments) {
        try {
          await deleteObject(ref(storage, attachment.storagePath));
        } catch (storageError) {
          console.warn('Could not delete attachment:', storageError);
        }
      }
    }

    // Delete from Firestore
    await deleteDoc(doc(db, WIKI_COLLECTION, entryId));

    // Remove this entry from all related entries' relationships
    const allEntries = await getAllWikiEntries();
    for (const otherEntry of allEntries) {
      if (otherEntry.relationships?.some((rel) => rel.entryId === entryId)) {
        const updatedRelationships = otherEntry.relationships.filter(
          (rel) => rel.entryId !== entryId
        );
        await updateWikiEntry(otherEntry.id, { relationships: updatedRelationships });
      }
    }
  } catch (error) {
    console.error('Error deleting wiki entry:', error);
    throw error;
  }
};

/**
 * Gets a single wiki entry
 * @param {string} entryId - The entry ID
 * @returns {Promise<object>} Entry data with ID
 */
export const getWikiEntry = async (entryId) => {
  try {
    const docRef = doc(db, WIKI_COLLECTION, entryId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    throw new Error('Entry not found');
  } catch (error) {
    console.error('Error fetching wiki entry:', error);
    throw error;
  }
};

/**
 * Gets all wiki entries (optionally filtered by type)
 * @param {string} type - Optional: 'location', 'person', or 'item'
 * @returns {Promise<array>} Array of entries
 */
export const getAllWikiEntries = async (type = null) => {
  try {
    let q;
    if (type) {
      q = query(collection(db, WIKI_COLLECTION), where('type', '==', type));
    } else {
      q = query(collection(db, WIKI_COLLECTION), orderBy('createdAt', 'desc'));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching wiki entries:', error);
    throw error;
  }
};

/**
 * Searches wiki entries by title and description
 * @param {string} searchTerm - Text to search for
 * @returns {Promise<array>} Matching entries
 */
export const searchWikiEntries = async (searchTerm) => {
  try {
    const allEntries = await getAllWikiEntries();
    const lowerSearch = searchTerm.toLowerCase();

    return allEntries.filter(
      (entry) =>
        entry.title.toLowerCase().includes(lowerSearch) ||
        entry.description.toLowerCase().includes(lowerSearch)
    );
  } catch (error) {
    console.error('Error searching wiki entries:', error);
    throw error;
  }
};

/**
 * Gets entries filtered by tags
 * @param {array} tags - Array of tag strings
 * @returns {Promise<array>} Matching entries
 */
export const getEntriesByTags = async (tags) => {
  try {
    const allEntries = await getAllWikiEntries();
    return allEntries.filter((entry) =>
      tags.some((tag) => entry.tags?.includes(tag))
    );
  } catch (error) {
    console.error('Error filtering by tags:', error);
    throw error;
  }
};

/**
 * Get the inverse relationship type for bidirectional relationships
 * @param {string} relationshipType - The original relationship type
 * @param {string} fromType - The type of the source entry (optional, for context-aware inversions)
 * @returns {string|null} The inverse relationship type, or null if no inverse exists
 */
const getInverseRelationship = (relationshipType, fromType = null) => {
  const inverseMap = {
    // Location relationships
    'part_of': 'contains',
    'contains': 'part_of',
    'parent_location': 'child_location',
    'child_location': 'parent_location',
    'within': 'contains',
    
    // Person relationships
    'parent': 'child',
    'child': 'parent',
    'spouse': 'spouse',
    'sibling': 'sibling',
    'leader': 'subordinate',
    'subordinate': 'leader',
    'mentor': 'student',
    'student': 'mentor',
    'ally': 'ally_of',
    'ally_of': 'ally',
    'enemy': 'enemy_of',
    'enemy_of': 'enemy',
    'owns_item': 'owned_by',
    'previously_owned': 'previously_owned_by',
    'lives_in': 'home_to',
    'works_at': 'workplace_of',
    'member_of': 'has_member',
    'leads': 'led_by',
    'founded_by': 'founded',
    
    // Item relationships
    'owned_by': 'owns_item',
    'previously_owned_by': 'previously_owned',
    'located_at': 'contains',
    'found_in': 'contains',
    'hidden_in': 'contains',
    'owns_location': 'owned_by',
    
    // Monster relationships
    'hunted_by': 'hunts',
    'hunts': 'hunted_by',
    'commanded_by': 'commands',
    'commands': 'commanded_by',
    'summoned_by': 'summoned',
    'summoned': 'summoned_by',
    'defeated_by': 'defeated',
    'defeated': 'defeated_by',
    'serves': 'served_by',
    'served_by': 'serves',
    'inhabits': 'home_to',
    'guards': 'protected_by',
    'threatens': 'threatened_by',
    'threatened_by': 'threatens',
    'protected_by': 'protects',
    'protects': 'protected_by',
    'encountered_at': 'encounter_with',
    'encounter_with': 'encountered_at',
    
    // Faction relationships
    'has_member': 'member_of',
    'led_by': 'leads',
    'founded': 'founded_by',
    'allied_with': 'allied_with',
    'rival_of': 'rival_of',
    'at_war_with': 'at_war_with',
    'controls': 'controlled_by',
    'controlled_by': 'controls',
    'based_in': 'headquarters_of',
    'headquarters_of': 'based_in',
    'owns': 'owned_by_faction',
    'owned_by_faction': 'owns',
    'sponsors': 'sponsored_by',
    'sponsored_by': 'sponsors',
    'opposes': 'opposed_by',
    'opposed_by': 'opposes',
    'parent_faction': 'subfaction_of',
    'subfaction_of': 'parent_faction',
    
    // Generic
    'related': 'related',
    'mentioned_in': 'mentions',
    'mentions': 'mentioned_in',
    'appeared_in': 'features',
    'features': 'appeared_in',
  };
  
  // Handle context-specific inversions for 'owned_by'
  if (relationshipType === 'owned_by') {
    if (fromType === 'location') {
      return 'owns_location';
    } else if (fromType === 'item') {
      return 'owns_item';
    }
  }
  
  return inverseMap[relationshipType] || null;
};

/**
 * Adds a relationship between two entries
 * @param {string} fromEntryId - Source entry ID
 * @param {string} toEntryId - Target entry ID
 * @param {string} relationshipType - Type of relationship (e.g., 'related', 'owned_by', 'ally', 'family')
 * @returns {Promise<void>}
 */
export const addRelationship = async (fromEntryId, toEntryId, relationshipType) => {
  try {
    // Get the source entry to determine its type for context-aware inverse relationships
    const fromEntry = await getWikiEntry(fromEntryId);
    
    const entryRef = doc(db, WIKI_COLLECTION, fromEntryId);
    const relationship = {
      entryId: toEntryId,
      type: relationshipType,
      createdAt: Timestamp.now(),
    };

    await updateDoc(entryRef, {
      relationships: arrayUnion(relationship),
    });

    // Check if there's an inverse relationship to create
    const inverseType = getInverseRelationship(relationshipType, fromEntry.type);
    if (inverseType) {
      const targetEntryRef = doc(db, WIKI_COLLECTION, toEntryId);
      const inverseRelationship = {
        entryId: fromEntryId,
        type: inverseType,
        createdAt: Timestamp.now(),
      };

      await updateDoc(targetEntryRef, {
        relationships: arrayUnion(inverseRelationship),
      });
    }
  } catch (error) {
    console.error('Error adding relationship:', error);
    throw error;
  }
};

/**
 * Removes a relationship between two entries (and the inverse relationship)
 * @param {string} fromEntryId - Source entry ID
 * @param {string} toEntryId - Target entry ID
 * @returns {Promise<void>}
 */
export const removeRelationship = async (fromEntryId, toEntryId) => {
  try {
    // Remove the forward relationship
    const entry = await getWikiEntry(fromEntryId);
    const updatedRelationships = entry.relationships.filter(
      (rel) => rel.entryId !== toEntryId
    );
    await updateWikiEntry(fromEntryId, { relationships: updatedRelationships });

    // Remove the inverse relationship
    try {
      const targetEntry = await getWikiEntry(toEntryId);
      if (targetEntry.relationships && targetEntry.relationships.length > 0) {
        const updatedTargetRelationships = targetEntry.relationships.filter(
          (rel) => rel.entryId !== fromEntryId
        );
        await updateWikiEntry(toEntryId, { relationships: updatedTargetRelationships });
      }
    } catch (inverseError) {
      // If removing inverse fails, log but don't throw (forward relationship was still removed)
      console.warn('Could not remove inverse relationship:', inverseError);
    }
  } catch (error) {
    console.error('Error removing relationship:', error);
    throw error;
  }
};

/**
 * Uploads an image for a wiki entry
 * @param {string} entryId - The entry ID
 * @param {File} file - The image file
 * @returns {Promise<string>} Download URL
 */
export const uploadWikiImage = async (entryId, file) => {
  try {
    if (!entryId || !file) {
      throw new Error('Entry ID and file are required');
    }

    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '');
    const imagePath = `wikiImages/${entryId}/${sanitizedFileName || 'image'}`;
    const storageRef = ref(storage, imagePath);

    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading wiki image:', error);
    throw error;
  }
};

/**
 * Uploads a document attachment for a wiki entry
 * @param {string} entryId - The entry ID
 * @param {File} file - The document file
 * @returns {Promise<object>} Attachment metadata { name, url, size, type, uploadedAt }
 */
export const uploadWikiAttachment = async (entryId, file) => {
  try {
    if (!entryId || !file) {
      throw new Error('Entry ID and file are required');
    }

    // Validate file size (25MB max)
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size must be less than 25MB');
    }

    // Validate file type (common document types)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not supported. Allowed: PDF, Word, Excel, PowerPoint, Text, CSV, and Images');
    }

    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const timestamp = Date.now();
    const attachmentPath = `wikiAttachments/${entryId}/${timestamp}_${sanitizedFileName}`;
    const storageRef = ref(storage, attachmentPath);

    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    const attachmentData = {
      name: file.name,
      url: downloadURL,
      storagePath: attachmentPath,
      size: file.size,
      type: file.type,
      uploadedAt: Timestamp.now()
    };

    // Add to entry's attachments array in Firestore
    const entry = await getWikiEntry(entryId);
    const updatedAttachments = [...(entry.attachments || []), attachmentData];
    await updateWikiEntry(entryId, { attachments: updatedAttachments });

    return attachmentData;
  } catch (error) {
    console.error('Error uploading wiki attachment:', error);
    throw error;
  }
};

/**
 * Deletes an attachment from storage and updates the entry
 * @param {string} entryId - The entry ID
 * @param {string} storagePath - The storage path of the attachment
 * @returns {Promise<void>}
 */
export const deleteWikiAttachment = async (entryId, storagePath) => {
  try {
    // Delete from storage
    await deleteObject(ref(storage, storagePath));

    // Remove from entry's attachments array
    const entry = await getWikiEntry(entryId);
    const updatedAttachments = (entry.attachments || []).filter(
      att => att.storagePath !== storagePath
    );
    await updateWikiEntry(entryId, { attachments: updatedAttachments });
  } catch (error) {
    console.error('Error deleting wiki attachment:', error);
    throw error;
  }
};

/**
 * Gets all available tags across all entries
 * @returns {Promise<array>} Array of unique tags
 */
export const getAllTags = async () => {
  try {
    const allEntries = await getAllWikiEntries();
    const tagsMap = new Map();

    allEntries.forEach((entry) => {
      if (entry.tags?.length > 0) {
        entry.tags.forEach((tag) => {
          const lowerTag = tag.toLowerCase().trim();
          // Keep the first occurrence's casing, ignore duplicates
          if (!tagsMap.has(lowerTag)) {
            tagsMap.set(lowerTag, tag.trim());
          }
        });
      }
    });

    return Array.from(tagsMap.values()).sort((a, b) => 
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  } catch (error) {
    console.error('Error fetching tags:', error);
    throw error;
  }
};

/**
 * Gets entries by type and filters
 * @param {string} type - 'location', 'person', or 'item'
 * @param {object} filters - { tags: [], searchTerm: '' }
 * @returns {Promise<array>} Filtered entries
 */
export const getFilteredEntries = async (type, filters = {}) => {
  try {
    let entries = await getAllWikiEntries(type);

    // Apply search filter
    if (filters.searchTerm) {
      const lowerSearch = filters.searchTerm.toLowerCase();
      entries = entries.filter(
        (entry) =>
          entry.title.toLowerCase().includes(lowerSearch) ||
          entry.description.toLowerCase().includes(lowerSearch)
      );
    }

    // Apply tag filter
    if (filters.tags?.length > 0) {
      entries = entries.filter((entry) =>
        filters.tags.some((tag) => entry.tags?.includes(tag))
      );
    }

    return entries;
  } catch (error) {
    console.error('Error filtering entries:', error);
    throw error;
  }
};
